import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Download, Package, FileText, Loader2 } from 'lucide-react';
import { useEntitlements } from '../../hooks/useEntitlements';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getDownloadUrl, downloadFile } from '../../services/downloads';
import type { Product } from '../../types';

interface ProductWithFiles extends Product {
  downloading?: Set<string>;
}

export default function Downloads() {
  const { entitlements, loading: entitlementsLoading } = useEntitlements();
  const [products, setProducts] = useState<ProductWithFiles[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchProducts = async () => {
      if (entitlements.length === 0) {
        setProducts([]);
        setLoading(false);
        return;
      }

      try {
        const productIds = entitlements.map(e => e.productId);
        // Firestore 'in' operator has a limit of 10 items
        const batches = [];
        for (let i = 0; i < productIds.length; i += 10) {
          const batch = productIds.slice(i, i + 10);
          const productsQuery = query(
            collection(db, 'products'),
            where('__name__', 'in', batch)
          );
          batches.push(getDocs(productsQuery));
        }

        const results = await Promise.all(batches);
        const allProducts: Product[] = [];
        results.forEach(snapshot => {
          snapshot.forEach(doc => {
            allProducts.push({ id: doc.id, ...doc.data() } as Product);
          });
        });

        setProducts(allProducts);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [entitlements]);

  const handleDownload = async (storagePath: string, fileKey: string, fileName: string) => {
    const downloadKey = fileKey;
    setDownloadingFiles(prev => new Set(prev).add(downloadKey));

    try {
      const url = await getDownloadUrl(storagePath);
      downloadFile(url, fileName);
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download file. Please try again.');
    } finally {
      setDownloadingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(downloadKey);
        return newSet;
      });
    }
  };

  if (entitlementsLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your downloads...</p>
        </div>
      </div>
    );
  }

  if (entitlements.length === 0) {
    return (
      <div className="space-y-8">
        <h1 className="text-3xl font-bold text-gray-900">My Downloads</h1>

        <div className="bg-white rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
          <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-900 mb-2">
            No products yet!
          </h3>
          <p className="text-gray-600 mb-6">
            Explore our products to get started with legal templates and documents
          </p>
          <Link
            to="/portal/products"
            className="inline-flex items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            Browse Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">My Downloads</h1>
        <p className="mt-2 text-gray-600">
          Access all your legal templates and documents
        </p>
      </div>

      {/* Products grid */}
      <div className="space-y-6">
        {products.map((product) => (
          <div
            key={product.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
          >
            {/* Product header */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{product.name}</h2>
                  {product.description && (
                    <p className="mt-1 text-sm text-gray-600">{product.description}</p>
                  )}
                </div>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  ✓ Owned
                </span>
              </div>
            </div>

            {/* Files list */}
            <div className="p-6">
              {product.files && product.files.length > 0 ? (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-700 mb-3">
                    Available files ({product.files.length}):
                  </p>
                  <div className="grid gap-3">
                    {product.files.map((file) => {
                      const isDownloading = downloadingFiles.has(file.key);

                      return (
                        <div
                          key={file.key}
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all"
                        >
                          <div className="flex items-center flex-1 min-w-0">
                            <FileText className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {file.label}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {file.storagePath.split('/').pop()}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDownload(file.storagePath, file.key, file.label)}
                            disabled={isDownloading}
                            className="ml-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {isDownloading ? (
                              <>
                                <Loader2 className="animate-spin h-4 w-4 mr-2" />
                                Downloading...
                              </>
                            ) : (
                              <>
                                <Download className="h-4 w-4 mr-2" />
                                Download
                              </>
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">
                  No files available for this product yet
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Help text */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> You can download your files as many times as you need while your account is active. Files are protected by secure access controls.
        </p>
      </div>
    </div>
  );
}
